import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line,
} from "recharts";

// Palette aligned with app semantic colors
export const COLORS = {
  positive: "#059669", // emerald-600
  baseline: "#1E3A8A", // navy / blue-900
  info:     "#0EA5E9", // sky-500 (for secondary bar)
  warning:  "#F59E0B", // amber-500
  risk:     "#DC2626", // red-600
  muted:    "#94A3B8", // slate-400
};

const rp = (v) =>
  typeof v === "number"
    ? new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(v)
    : v;
const nf = (v) => (typeof v === "number" ? new Intl.NumberFormat("id-ID").format(v) : v);

function EmptyState({ hint = "Data belum tersedia." }) {
  return (
    <div className="h-56 flex items-center justify-center text-sm text-slate-400 italic">
      {hint}
    </div>
  );
}

function ChartCard({ title, children, testId }) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-slate-700">{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-2">{children}</CardContent>
    </Card>
  );
}

/** Donut: value/{name,value,color}[] */
export function DonutCard({ title, data, testId, formatter = nf, hint }) {
  const total = (data || []).reduce((s, d) => s + (Number(d.value) || 0), 0);
  if (!data || total <= 0) {
    return <ChartCard title={title} testId={testId}><EmptyState hint={hint} /></ChartCard>;
  }
  return (
    <ChartCard title={title} testId={testId}>
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name"
               innerRadius={55} outerRadius={85} paddingAngle={2}>
            {data.map((d, i) => (<Cell key={i} fill={d.color || COLORS.baseline} />))}
          </Pie>
          <Tooltip formatter={(v, n) => [formatter(v), n]} />
          <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
      <div className="text-center text-xs text-slate-500 -mt-2">Total: {formatter(total)}</div>
    </ChartCard>
  );
}

/** Horizontal bar: top-N category ranking */
export function HorizontalBarCard({ title, data, bars, testId, hint, currency = false, height = 260 }) {
  const hasData = Array.isArray(data) && data.some((d) =>
    bars.some((b) => (Number(d[b.key]) || 0) > 0));
  if (!hasData) {
    return <ChartCard title={title} testId={testId}><EmptyState hint={hint} /></ChartCard>;
  }
  const fmt = currency ? rp : nf;
  return (
    <ChartCard title={title} testId={testId}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} layout="vertical" margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11, fill: "#475569" }}
                 tickFormatter={(v) => currency ? new Intl.NumberFormat("id-ID", { notation: "compact" }).format(v) : nf(v)} />
          <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11, fill: "#334155" }} />
          <Tooltip formatter={(v, n) => [fmt(v), n]} />
          {bars.map((b) => (
            <Bar key={b.key} dataKey={b.key} name={b.label} fill={b.color || COLORS.baseline} radius={[0, 4, 4, 0]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

/** Line: time-series data=[{name, ...}], lines=[{key,label,color}] */
export function LineCard({ title, data, lines, testId, hint, currency = false, height = 260 }) {
  const hasData = Array.isArray(data) && data.length >= 2 && data.some((d) =>
    lines.some((l) => (Number(d[l.key]) || 0) > 0));
  if (!hasData) {
    return <ChartCard title={title} testId={testId}><EmptyState hint={hint} /></ChartCard>;
  }
  const fmt = currency ? rp : nf;
  return (
    <ChartCard title={title} testId={testId}>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} />
          <YAxis tick={{ fontSize: 11, fill: "#475569" }}
                 tickFormatter={(v) => currency ? new Intl.NumberFormat("id-ID", { notation: "compact" }).format(v) : nf(v)} />
          <Tooltip formatter={(v, n) => [fmt(v), n]} />
          {lines.length > 1 && <Legend iconType="plainline" wrapperStyle={{ fontSize: 12 }} />}
          {lines.map((l) => (
            <Line key={l.key} type="monotone" dataKey={l.key} name={l.label}
                  stroke={l.color || COLORS.baseline} strokeWidth={2} dot={{ r: 3 }} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function BarCard({ title, data, bars, testId, formatter = nf, hint, currency = false }) {
  const hasData = Array.isArray(data) && data.some((d) =>
    bars.some((b) => (Number(d[b.key]) || 0) > 0));
  if (!hasData) {
    return <ChartCard title={title} testId={testId}><EmptyState hint={hint} /></ChartCard>;
  }
  const fmt = currency ? rp : formatter;
  return (
    <ChartCard title={title} testId={testId}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} />
          <YAxis tick={{ fontSize: 11, fill: "#475569" }}
                 tickFormatter={(v) => currency ? new Intl.NumberFormat("id-ID", { notation: "compact" }).format(v) : nf(v)} />
          <Tooltip formatter={(v, n) => [fmt(v), n]} />
          {bars.length > 1 && <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} />}
          {bars.map((b) => (
            <Bar key={b.key} dataKey={b.key} name={b.label} fill={b.color || COLORS.baseline} radius={[4, 4, 0, 0]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
